import React, { useState } from 'react';
import { get, has } from 'lodash';
import { Box, Button, FormHelperText, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { DirectUpload } from 'activestorage';
import {
  AddAPhoto,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

import BaseImage from '../../DesignSystem/BaseImage';
import EmptyState from '../../DesignSystem/EmptyState';
import Tooltip from '../../DesignSystem/Tooltip';
import { cloudinaryImageUrl } from '../../../Services/Cloudinary';
import appsignal from '../../../Services/ErrorReportingService';
import withOptional from '../../../Utils/Posts/Form/withOptional';

const FILE_SIZE_LIMIT = 10; // MB

function PicturePicker({
  uiSectionId,
  pictureS3Key,
  name = 'picture',
  filename,
  rounded = false,
  round = false,
  errors,
  register,
  setValue,
  willDestroy = false,
  label,
  tooltip = null,
  recommandedSize = [1200, 800],
  previewSize = [500, 500],
  previewRatio = 'fullHeight',
  setDisabledForm,
  setError,
  required = false,
}: any) {
  const intl = useIntl();
  const [pictureObject, setPictureObject] = useState<string | null>(null);
  const pictureUrl = cloudinaryImageUrl(
    pictureS3Key,
    `w_${previewSize[0]},h_${previewSize[1]},c_crop,c_fill,q_auto`,
    filename,
  );
  const labelOrPlaceholder = label || intl.formatMessage({ id: 'PICTURE_PICKER.COVER' });

  const handleTriggerFileInput = () => {
    document.getElementById(`${uiSectionId}-${name}-PickerInputFile`)?.click();
  };

  const handleDeletePicture = () => {
    setPictureObject(null);
    setValue(`${name}_attributes[_destroy]`, true);
    setValue(name, null);
  };

  const handleOnChangeFileInput = (event: any) => {
    const file = event.target.files[0];

    setDisabledForm(true);

    if (file.size > FILE_SIZE_LIMIT * 1024 * 1000) {
      setError(name, {
        type: 'manual',
        message: intl.formatMessage(
          { id: 'POSTS.FORM.PICTURE.ERRORS.FILE_SIZE' },
          { fileSize: FILE_SIZE_LIMIT },
        ),
      });
      setDisabledForm(false);
    } else {
      const upload = new DirectUpload(file, '/api/direct_uploads');

      upload.create((error: any, blob: any) => {
        if (error) {
          appsignal.sendError(error);
          setError(name, error);
        } else {
          setValue(name, blob.blob_signed_id);

          setDisabledForm(false);
        }
      });

      setPictureObject(URL.createObjectURL(file));
      setValue(`${name}_attributes[_destroy]`, false);
    }
  };

  const pictureUrlToRender = willDestroy ? null : pictureObject || pictureUrl;

  return (
    <>
      <input type="hidden" {...register} name={uiSectionId} />
      <input type="hidden" {...register} name={`${name}_attributes[_destroy]`} />
      <Box display="flex" alignItems="center" mb={2}>
        <Typography
          variant="body1"
          color={has(errors, `${name}.message`) ? 'error' : 'textSecondary'}
          id={`${uiSectionId}-${name}-picker-helper-label`}
          sx={(theme) => ({ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) })}
          gutterBottom
          component="label"
        >
          {withOptional(labelOrPlaceholder, required)}

          {labelOrPlaceholder && (
            <Tooltip
              title={
                <Typography variant="body1" color="textPrimary">
                  {tooltip && (
                    <>
                      {tooltip}
                      <br />
                      <br />
                    </>
                  )}
                  <FormattedMessage
                    id="POSTS.FORM.PICTURE.TOOLTIP"
                    values={{
                      width: recommandedSize[0],
                      height: recommandedSize[1],
                      br: () => <br />,
                      b: (chunks: any) => <b>{chunks}</b>,
                    }}
                  />
                </Typography>
              }
              icon={<HelpIcon color="action" fontSize="small" />}
            />
          )}
        </Typography>
      </Box>

      <input
        id={`${uiSectionId}-${name}-PickerInputFile`}
        accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
        aria-describedby={`${uiSectionId}-${name}-picker-helper-description`}
        aria-labelledby={`${uiSectionId}-${name}-picker-helper-label`}
        className="fileInput"
        type="file"
        style={{ display: 'none' }}
        onClick={(e: any) => {
          e.target.value = null; // Allows to re-select the same file
        }}
        onChange={handleOnChangeFileInput}
      />

      {pictureUrlToRender && (
        <BaseImage
          src={pictureUrlToRender}
          ratio={previewRatio}
          alt={name.replace('_', ' ')}
          rounded={rounded}
          round={round}
          size={previewSize}
        />
      )}

      {(willDestroy || !pictureUrlToRender) && (
        <EmptyState
          ratio={previewRatio}
          action={
            <Button
              size="large"
              color="primary"
              variant="outlined"
              onClick={handleTriggerFileInput}
              startIcon={<AddAPhoto color="primary" />}
            >
              <FormattedMessage id="POSTS.FORM.PICTURE.BUTTON.ADD_PICTURE" />
            </Button>
          }
        />
      )}

      {pictureUrlToRender && (
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={2}
          mt={1}
        >
          <Button
            size="large"
            color="primary"
            onClick={handleTriggerFileInput}
            startIcon={<EditIcon color="primary" />}
          >
            <FormattedMessage id="BUTTONS.UPDATE" />
          </Button>

          {!willDestroy && pictureUrlToRender && (
            <Button
              size="large"
              color="primary"
              onClick={handleDeletePicture}
              startIcon={<DeleteIcon />}
            >
              <FormattedMessage id="POSTS.FORM.PICTURE.BUTTON.DELETE_PICTURE" />
            </Button>
          )}
        </Stack>
      )}

      <FormHelperText
        id={`${uiSectionId}-${name}-picker-helper-description`}
        error={has(errors, `${name}.message`)}
      >
        {get(errors, `${name}.message`)}
      </FormHelperText>
    </>
  );
}

export default PicturePicker;
